import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

interface RequestParams {
  token: string;
  lang: Lang;
}

const responseDataSchema = z.object({
  data: z.object({
    watchlistId: z.string(),
    watchlistName: z.string(),
    symbols: z.array(z.string()),
    active: z.boolean(),
    createdAt: z.string(),
  }),
  message: z.string(),
});

export type WatchlistData = z.infer<typeof responseDataSchema>["data"];

export const getActiveWatchlist = async ({
  token,
  lang,
}: RequestParams): Promise<WatchlistData> => {
  const endpoint = "/watchlist";
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: { "X-User-Profile": token, "Accept-Language": lang },
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return responseDataSchema.parse(await res.json()).data;
};

export const useActiveWatchlist = () => {
  const { backendToken: token } = useAuth();
  const { lang } = useLang();
  return useQuery(
    ["get-active-watchlist", token, lang],
    () => getActiveWatchlist({ token: token ?? "", lang }),
    { enabled: !!token }
  );
};
